import React, { useEffect } from 'react';
import { RadioChangeEvent } from 'antd';
import { useQuery } from '@common/hooks';
import { TiersFlatQuery, TiersFlatType } from '@institutions/graphql';
import {
  AlignedInfo,
  ComboRadio,
  ErrorHandler,
  Gutter,
  LoadingState,
} from '@common/components';
import { tierHumanizeVisibilityOffset } from '@institutions/services';

export type TieringRadioTierType = {
  id: string;
  name?: string;
  fee?: number;
  visibilityOffset?: number;
};

type Props = {
  headerSlot?: React.ReactNode;
  selectedTier: TieringRadioTierType;
  onTierChange: (value: TieringRadioTierType) => void;
};

export function TieringRadioWithOptions({
  headerSlot,
  selectedTier,
  onTierChange,
}: Props) {
  const { data, error, loading } = useQuery(TiersFlatQuery);

  useEffect(() => {
    if (!data) return;

    const selectedTierWithFullData = getTierById(data.tiers, selectedTier.id);

    onTierChange({ ...selectedTierWithFullData });
  }, [data, selectedTier.id, onTierChange]);

  if (error) {
    return <ErrorHandler error={error} log="TieringRadioWithOptions" />;
  }

  if (loading || !data) {
    return <LoadingState />;
  }

  const { tiers } = data;

  return (
    <div className="tiering-radio-with-options">
      {headerSlot && (
        <Gutter size="md" direction="bottom">
          {headerSlot}
        </Gutter>
      )}
      <ComboRadio
        options={tiers.map((tier) => ({
          open: true,
          label: tier.name,
          value: tier.id,
          content: (
            <AlignedInfo
              content={[
                [
                  'Staffing Request Delay',
                  tierHumanizeVisibilityOffset(tier.visibilityOffset),
                ],
                ['Admin Fee', `${tier.fee}%`],
              ]}
            />
          ),
        }))}
        value={selectedTier.id}
        onChange={(e: RadioChangeEvent) => {
          const selectedTier = getTierById(tiers, e.target.value);

          onTierChange(selectedTier);
        }}
      />
    </div>
  );
}

function getTierById(tiers: TiersFlatType[], tierId?: string) {
  return (
    tiers.find((tier: TieringRadioTierType) => tier.id === tierId) || {
      id: '',
    }
  );
}
